import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";

import Box from "veneer/Box";
import Button from "veneer/Button";
import DataTable, {
  BulkActionBarItem,
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import Stack from "veneer/Stack";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import {
  DRAWER_CREATE_CONTENT_GROUP,
  DRAWER_CREATE_FOLDER,
  DRAWER_MOVE_TO_FOLDER,
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_LIBRARY_URL,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import {
  contentFoldersSelector,
  contentFoldersStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/content-folders/selectors";
import {
  contentGroupsSelector,
  contentGroupsStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/content-groups/selectors";
import type { MediaLibraryStoreContentFolder } from "cortex/ui/src/fleet/view/media-library/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { useFeatureFlag } from "cortex/ui/src/hooks/useFeatureFlag";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { addErrorMessageAction } from "cortex/ui/src/redux-store/messages/actions";
import {
  userContentsSelector,
  userContentsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/user-contents/selectors";
import { usersSelector } from "cortex/ui/src/redux-store/users/selectors";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import { Container } from "cortex/ui/src/view/layout";

import ContentGroupDrawer from "./ContentGroupDrawer";
import CreateFolderDrawer from "./CreateFolderDrawer";
import MediaTableFilters from "./MediaTableFilters";
import MediaUploadButton from "./MediaUploadButton";
import MoveToFolderDrawer from "./MoveToFolderDrawer";
import type { ArchiveMediaHandler, ShowSidebarHandler } from "../action";
import { archiveMedia, showSidebarForRow } from "../action";
import { formatPreview } from "../format";
import { Filter, Media } from "../model";
import { getFolderUrl, getTableData } from "../utils";

const filter = createFilter<Media>("name");
const column = createColumnHelper<Media>();

function renderBreadcrumbs(
  folder: MediaLibraryStoreContentFolder | null,
  isStrategysEnabled: boolean,
) {
  if (!folder) {
    return undefined;
  }

  return getBreadcrumbs([
    {
      label: isStrategysEnabled ? "Media Library" : "Fallback Creatives",
      target: MEDIA_LIBRARY_URL,
    },
  ]);
}

function safeLocaleCompare(a?: string | null, b?: string | null) {
  const an = (a ?? "").trim();
  const bn = (b ?? "").trim();
  return an.localeCompare(bn, undefined, { sensitivity: "base" });
}

function getTitle(
  folderId: string,
  folder: MediaLibraryStoreContentFolder | null,
  isStrategysEnabled: boolean,
) {
  if (!folderId) {
    return isStrategysEnabled ? "Media Library" : "Fallback Creatives";
  }
  if (!folder) {
    return undefined;
  }
  return folder.name;
}

export default function MediaTable() {
  const isStrategiesEnabled = useFeatureFlag("is-strategies-enabled");
  const { id: folderId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useState<Filter>(() => ({
    tag: null,
    type: null,
  }));
  const [selectedItems, setSelectedItems] = useState<ReadonlyArray<Media>>([]);
  const [selectedDrawer, setSelectedDrawer] = useState<string | null>(null);
  const contentFolders = useAppSelector(contentFoldersSelector);
  const contentFoldersLoaded = useAppSelector(
    contentFoldersStoresLoadedSelector,
  );
  const contentGroups = useAppSelector(contentGroupsSelector);
  const contentGroupsLoaded = useAppSelector(contentGroupsStoresLoadedSelector);
  const userContents = useAppSelector(userContentsSelector);
  const userContentsLoaded = useAppSelector(userContentsStoresLoadedSelector);
  const applications = useAppSelector(applicationsSelector);
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const users = useAppSelector(usersSelector);
  const storesLoaded = useMemo(
    () =>
      applicationsStoresLoaded &&
      contentFoldersLoaded &&
      contentGroupsLoaded &&
      userContentsLoaded,
    [
      applicationsStoresLoaded,
      contentFoldersLoaded,
      contentGroupsLoaded,
      userContentsLoaded,
    ],
  );

  const folder = useMemo(() => {
    if (!storesLoaded || !folderId || !contentFolders?.byId) {
      return null;
    }
    return contentFolders.byId[folderId] || null;
  }, [contentFolders?.byId, folderId, storesLoaded]);

  const allRows = useMemo<ReadonlyArray<Media>>(() => {
    if (!storesLoaded) {
      return [];
    }
    return getTableData({
      applications,
      contentFolders,
      contentGroups,
      userContents,
      includeMediaTypes: [
        MEDIA_ENTRY_APPLICATION,
        MEDIA_ENTRY_CONTENT_GROUP,
        MEDIA_ENTRY_FOLDER,
        MEDIA_ENTRY_USER_CONTENT,
      ],
    }) as ReadonlyArray<Media>;
  }, [applications, contentFolders, contentGroups, storesLoaded, userContents]);

  const visibleRows = useMemo<ReadonlyArray<Media>>(() => {
    const base = !folderId
      ? allRows.filter((r) => !r.folder_id)
      : allRows.filter((r) => r.folder_id === Number(folderId));
    return base.filter(
      ({ friendlyType, tag }) =>
        (!filters.tag || tag === filters.tag) &&
        (!filters.type || friendlyType === filters.type),
    ) as ReadonlyArray<Media>;
  }, [allRows, folderId, filters]);

  const folderOptions = useMemo(() => {
    try {
      const safeRows = Array.isArray(allRows) ? allRows : [];
      const folders = safeRows.filter(
        (row) => row?.type === MEDIA_ENTRY_FOLDER,
      );
      return folders.sort((a, b) => safeLocaleCompare(a?.name, b?.name));
    } catch (e) {
      console.error("Failed to build folderOptions", e);
      return [];
    }
  }, [allRows]);

  useEffect(() => {
    setSelectedItems([]);
  }, [folderId]);

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          visibleRows
            .map((row) => row.friendlyType)
            .filter(
              (type) =>
                type &&
                type !== "Folder" &&
                type !== "Media Group" &&
                type !== "Web Page" &&
                type !== "Application",
            ),
        ),
      ).sort(),
    [visibleRows],
  );

  const handleArchiveMedia: ArchiveMediaHandler = useCallback(
    async (items: ReadonlyArray<Media>) => {
      try {
        await dispatch(archiveMedia(items));
      } catch (_err) {
        dispatch(addErrorMessageAction("Failed to archive media"));
      }
      setSelectedItems([]);
    },
    [dispatch],
  );

  const handeErrorMessage = useCallback(
    (message: string) => {
      dispatch(addErrorMessageAction(message));
    },
    [dispatch],
  );

  const handleShowSidebar: ShowSidebarHandler = useCallback(
    (row) => {
      dispatch(showSidebarForRow(row));
    },
    [dispatch],
  );

  const handleCloseDrawer = useCallback(() => {
    setSelectedDrawer(null);
  }, []);

  const columns = useMemo(
    () => [
      column.accessor("url", {
        header: "Preview",
        cell: (prop) => formatPreview(prop.getValue(), prop.row.original),
        enableSorting: false,
        meta: {
          align: "center",
          width: 125,
        },
      }),
      column.accessor("name", {
        header: "Name",
        cell: (prop) => {
          const row = prop.row.original;
          const value = prop.getValue() ?? "Untitled";
          if (row.type !== MEDIA_ENTRY_FOLDER) {
            return value;
          }
          const folderUrl = getFolderUrl(row.id);
          return <Link to={folderUrl}>{value}</Link>;
        },
        sortingFn: "alphanumeric",
      }),
      column.accessor("friendlyType", {
        header: "Type",
        cell: (prop) => prop.getValue() || "N/A",
        sortingFn: "alphanumeric",
        meta: {
          width: 150,
        },
      }),
      column.accessor("upload_date", {
        header: "Uploaded",
        cell: (prop) => formatDateTimeLocal(prop.getValue()),
        sortingFn: sorting.datetime((row: Media) => row.upload_date),
        meta: {
          width: 200,
        },
      }),
      column.accessor("user_id", {
        header: "Uploaded By",
        cell: (prop) => {
          const userId = prop.getValue();
          const user = users[userId || ""];
          return user ? (
            <>
              {user?.name}
              <br />
              {`<${user.email}>`}
            </>
          ) : (
            ""
          );
        },
        sortingFn: sorting.number((row: Media) => row.user_id || 0),
        meta: {
          width: "fit-content",
        },
      }),
    ],
    [users],
  );

  const filterOptions = useMemo(
    () => (
      <MediaTableFilters
        onChange={setFilters}
        typeOptions={typeOptions}
        value={filters}
      />
    ),
    [filters, typeOptions],
  );

  const bulkActionBarItems = useMemo<ReadonlyArray<BulkActionBarItem>>(
    () => [
      {
        label: "Archive",
        onClick: () => handleArchiveMedia(selectedItems),
      },
      {
        label: "Move to Folder",
        onClick: () => setSelectedDrawer(DRAWER_MOVE_TO_FOLDER),
      },
    ],
    [handleArchiveMedia, selectedItems, setSelectedDrawer],
  );

  const addButton = (
    <Stack direction="row" spacing={1}>
      <Button
        onClick={() => setSelectedDrawer(DRAWER_CREATE_CONTENT_GROUP)}
        variant="outlined"
      >
        Add Media Group
      </Button>
      {!folderId && (
        <Button
          onClick={() => setSelectedDrawer(DRAWER_CREATE_FOLDER)}
          variant="outlined"
        >
          Add Folder
        </Button>
      )}
      <MediaUploadButton
        folderId={folder?.id}
        key="upload-button"
        onError={handeErrorMessage}
      />
    </Stack>
  );

  const title = getTitle(folderId, folder, isStrategiesEnabled);

  return (
    <Container
      breadcrumbs={renderBreadcrumbs(folder, isStrategiesEnabled)}
      title={title}
    >
      {storesLoaded || (folderId && folder) ? (
        <DataTable
          bulkActionItems={bulkActionBarItems}
          columns={columns}
          emptyState={
            <Box p={2} textAlign="center">
              <Typography color={Colors.gray1000} variant="body2">
                There is no data to display
              </Typography>
            </Box>
          }
          filter={filter}
          getIdentifier={({ id }: Media) => String(id)}
          getRowProps={(row: Media) => ({
            hover: true,
            onClick: (e: MouseEvent) => {
              if ((e.target as HTMLElement).closest("input[type='checkbox']")) {
                return;
              }
              handleShowSidebar(row);
            },
          })}
          initialPageSize={10}
          initialSort={{ id: "upload_date", desc: true }}
          primaryActions={addButton}
          rows={visibleRows}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          viewOptions={filterOptions}
        />
      ) : (
        <LoadingIndicator data-testid="loading-indicator" />
      )}
      <CreateFolderDrawer
        onCancel={handleCloseDrawer}
        open={selectedDrawer === DRAWER_CREATE_FOLDER}
      />
      <MoveToFolderDrawer
        folderId={folderId ?? null}
        folders={folderOptions}
        onCancel={handleCloseDrawer}
        open={selectedDrawer === DRAWER_MOVE_TO_FOLDER}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
      {selectedDrawer === DRAWER_CREATE_CONTENT_GROUP && (
        <ContentGroupDrawer
          key={DRAWER_CREATE_CONTENT_GROUP}
          onCancel={handleCloseDrawer}
          open={selectedDrawer === DRAWER_CREATE_CONTENT_GROUP}
        />
      )}
    </Container>
  );
}
